/**
 * Resource Payload Debugger
 *
 * Centralized debug logging for resource payloads (sources, sinks, etc.)
 * before they are sent to the API.
 *
 * SAFETY: This module is compile-time guarded. Debug features only work in development.
 * Even if GrowthBook flag is accidentally enabled in production, this code won't run.
 *
 * CONFIGURATION (in order of precedence):
 * 1. Environment variable (build-time, highest priority):
 *    REACT_APP_RESOURCE_DEBUGGER='{"enabled":true,"debug_level":1,"block_api_calls":true}'
 *
 * 2. GrowthBook feature flag (runtime):
 *    UI_ENABLE_DEBUGGER: { enabled, debug_level, block_api_calls }
 *
 * Usage:
 *   # Command line - just log payloads:
 *   REACT_APP_RESOURCE_DEBUGGER='{"enabled":true}' yarn start
 *
 *   # Command line - log and block API calls:
 *   REACT_APP_RESOURCE_DEBUGGER='{"enabled":true,"block_api_calls":true}' yarn start
 *
 *   # Command line - verbose logging:
 *   REACT_APP_RESOURCE_DEBUGGER='{"enabled":true,"debug_level":3}' yarn start
 */

import { getFeatureFlagValue, DebugConfig } from 'new/constants/featureFlags';

/**
 * Compile-time guard: Debug features only work in development.
 * This ensures debug code is stripped from production builds and
 * prevents accidental blocking of API calls in production.
 */
const IS_DEVELOPMENT = process.env.NODE_ENV === 'development';

/**
 * Environment variable override (checked at build time, baked into bundle).
 * Takes precedence over GrowthBook settings.
 * Format: JSON object matching DebugConfig structure
 */
const ENV_DEBUGGER_CONFIG: DebugConfig | null = (() => {
  const envValue = process.env.REACT_APP_RESOURCE_DEBUGGER;
  if (!envValue) return null;
  try {
    return JSON.parse(envValue) as DebugConfig;
  } catch {
    console.warn('[ResourcePayloadDebugger] Invalid REACT_APP_RESOURCE_DEBUGGER JSON:', envValue);
    return null;
  }
})();

export type ResourceAction =
  | 'CREATE_SOURCE'
  | 'UPDATE_SOURCE'
  | 'UPDATE_SOURCE_TRIGGERS'
  | 'CREATE_SINK'
  | 'UPDATE_SINK'
  | 'CREATE_CREDENTIAL'
  | 'UPDATE_CREDENTIAL'
  | 'CREATE_DATASET'
  | 'UPDATE_DATASET';

interface DebugContext {
  endpoint?: string;
  resourceType?: string;
  resourceId?: string | number;
  connectionCategory?: string;
  flowType?: string;
  credentialId?: string | number;
  [key: string]: any;
}

/**
 * Debug log a resource payload right before it's sent to the API.
 * Only logs when UI_ENABLE_DEBUGGER feature flag is enabled.
 *
 * @param action - The action type (CREATE_SOURCE, UPDATE_SINK, etc.)
 * @param payload - The full payload being sent to the API
 * @param context - Additional context (endpoint, resource type, etc.)
 * @returns true if the API call should be blocked (debug mode with block_api_calls enabled)
 */
export function debugResourcePayload(
  action: ResourceAction,
  payload: Record<string, any>,
  context: DebugContext = {},
): boolean {
  // Compile-time guard: never run in production
  if (!IS_DEVELOPMENT) {
    return false;
  }

  // Check if debugging is enabled (ENV vars or GrowthBook)
  if (!isResourceDebugEnabled()) {
    return false;
  }

  const shouldBlockApiCall = shouldBlockResourceApiCalls();
  const debugLevel = getEffectiveDebugLevel();
  const { endpoint, resourceType, resourceId, connectionCategory, flowType, ...extraContext } =
    context;

  // Helper to log with level prefix
  const log = (level: number, message: string, data?: any) => {
    if (level > debugLevel) return;
    const prefix = `[DEBUG-${level}]`;
    if (data !== undefined) {
      console.log(`${prefix} ${message}`, data);
    } else {
      console.log(`${prefix} ${message}`);
    }
  };

  // Level 0: Always show the main payload info
  const blockStatus = shouldBlockApiCall
    ? '🛑 BLOCKED - Simulating API failure (form will reset, you can save again)'
    : '✅ API CALL WILL FIRE';
  log(0, `\n========== ${action} PAYLOAD ==========`);
  log(0, blockStatus);
  log(0, `Endpoint: ${endpoint || 'N/A'}`);
  log(0, `Resource Type: ${resourceType || 'N/A'}`);
  if (resourceId) {
    log(0, `Resource ID: ${resourceId}`);
  }
  log(0, 'Full Payload:', payload);
  log(0, '==========================================\n');

  // Level 1: Show source_config or sink_config separately
  if (debugLevel >= 1) {
    if (payload.source_config) {
      log(1, '--- source_config ---');
      log(1, 'source_config:', payload.source_config);
    }
    if (payload.sink_config) {
      log(1, '--- sink_config ---');
      log(1, 'sink_config:', payload.sink_config);
    }
    if (payload.template_config) {
      log(1, '--- template_config ---');
      log(1, 'template_config:', payload.template_config);
    }
    if (payload.script_config) {
      log(1, '--- script_config ---');
      log(1, 'script_config:', payload.script_config);
    }
    if (connectionCategory) {
      log(1, 'Connection Category:', connectionCategory);
    }
    if (flowType) {
      log(1, 'Flow Type:', flowType);
    }
  }

  // Level 2: Show individual fields breakdown
  if (debugLevel >= 2) {
    log(2, '--- Field Breakdown ---');

    // Show top-level fields
    Object.entries(payload).forEach(([key, value]) => {
      if (!['source_config', 'sink_config', 'template_config', 'script_config'].includes(key)) {
        log(2, `  ${key}:`, value);
      }
    });

    // Show config fields if present
    const configKey = payload.source_config
      ? 'source_config'
      : payload.sink_config
        ? 'sink_config'
        : null;

    if (configKey && payload[configKey]) {
      log(2, `\n--- ${configKey} Fields ---`);
      Object.entries(payload[configKey]).forEach(([key, value]) => {
        log(2, `  ${key}:`, value);
      });
    }
  }

  // Level 3: Show extra context
  if (debugLevel >= 3 && Object.keys(extraContext).length > 0) {
    log(3, '--- Extra Context ---');
    Object.entries(extraContext).forEach(([key, value]) => {
      log(3, `  ${key}:`, value);
    });
  }

  return shouldBlockApiCall;
}

/**
 * Get the effective debug config.
 * Priority: ENV var > GrowthBook
 */
function getEffectiveConfig(): DebugConfig {
  // ENV var override (highest priority)
  if (ENV_DEBUGGER_CONFIG) {
    return ENV_DEBUGGER_CONFIG;
  }

  // Fall back to GrowthBook
  return getFeatureFlagValue('UI_ENABLE_DEBUGGER');
}

/**
 * Check if resource debugging is enabled.
 * Compile-time guarded: always returns false in production builds.
 * Priority: ENV var > GrowthBook
 */
export function isResourceDebugEnabled(): boolean {
  // Compile-time guard: never enable in production
  if (!IS_DEVELOPMENT) {
    return false;
  }

  const config = getEffectiveConfig();
  return config.enabled === true;
}

/**
 * Check if API calls should be blocked (debug mode with block_api_calls enabled).
 * Compile-time guarded: always returns false in production builds.
 * Priority: ENV var > GrowthBook
 */
export function shouldBlockResourceApiCalls(): boolean {
  // Compile-time guard: never block in production
  if (!IS_DEVELOPMENT) {
    return false;
  }

  const config = getEffectiveConfig();
  return config.enabled === true && config.block_api_calls === true;
}

/**
 * Get the effective debug level.
 * Priority: ENV var > GrowthBook
 */
function getEffectiveDebugLevel(): number {
  const config = getEffectiveConfig();
  return config.debug_level || 0;
}

export default debugResourcePayload;
